# Which operations must run inline instead of riding the socket→BullMQ path?
#
# Two shapes of secret force inline execution, and this one predicate covers
# both because the remedy is identical — keep the call off the queue:
#
#   (a) CREDENTIAL-BEARING: the args may carry a plaintext password (or other
#       login secret). A queued job payload holds the raw args, and
#       removeOnFail retains failed jobs in Redis — a credential at rest.
#
#   (b) RAW-BEARER-TOKEN-CONSUMING: the method reads access_control.token
#       VERBATIM — to revoke it (logout) or hand it to a downstream
#       (grantPublishedSession). queueSanitize strips the raw token from every
#       job payload at enqueue (tokens are hashed at rest), so if one of these
#       queued, the sanitizer would leave it holding an undefined token: logout
#       would silently revoke nothing (the browser clears local state while the
#       real token stays live), grantPublishedSession would fail outright. They
#       must run inline so the raw token survives the call (card 6a654c24…2b33).
#
# These calls must NEVER ride the socket→BullMQ path.
#
# This predicate is the SINGLE source of truth, used by BOTH sides:
#   - client: client_api.canUseSocketForAPI routes these over HTTP
#   - server: bootstrap's socket dispatcher forces them into direct exec,
#     so transport safety does not depend on the client's bundle version
#     (card 6a654be4e1fbec521cca2b27 — a stale or hostile client still
#     emits the socket message; the server must not enqueue it)
#
# operationId is "Model.method" (or "collection.method" for schema models).
# /auth/i intentionally also matches auth MODELS (AuthToken.*) and 'reauth'
# and 'reauthStatus' (both raw-token consumers too); over-matching only costs
# a call the queue — it runs inline instead.
isCredentialBearingOperation = (operationId) ->
  opId = String(operationId or '')
  return true if /auth/i.test opId
  return true if /\.login/i.test opId
  # The MFA self-service surface is (a): totpEnroll/totpConfirm/totpUnenroll,
  # emailOtpEnroll/Confirm/Unenroll and mfaReset carry typed codes, proofs and
  # recovery codes in their args — factor credentials, exactly the thing a
  # retained job payload must not hold. (The webauthn* siblings were already
  # caught by /auth/i above — "webauthn" contains "auth" — which is why the
  # passkey half of the ceremony ran inline while the code half queued: card
  # 6a9a26208286fc2eeb2087a3, where the queued totpEnroll's socket-shaped
  # MFA_REQUIRED broke the enrollment screen.) mfaStatus/mfaStatusOf carry no
  # secret but are cheap reads feeding the security screens; inline costs
  # nothing and keeps the whole surface on one transport.
  return true if /\.totp/i.test opId
  return true if /\.emailOtp/i.test opId
  return true if /\.mfa/i.test opId
  # Raw-bearer-token consumers (b): matched by method name so any auth model's
  # logout and the Deploy publish-grant stay inline regardless of model prefix.
  return true if /\.logout$/i.test opId
  return true if /\.grantPublishedSession$/i.test opId
  # Same (b) as the published grant: grantBupSession reads access_control.token
  # verbatim to copy the caller's session into a mounted backup's sibling DB.
  # queueSanitize strips the raw token at enqueue, so the socket path fails
  # with "no authenticated session" — found live, the masqueradeAs discovery
  # replayed on a new method.
  return true if /\.grantBupSession$/i.test opId
  # Same (b) again on the registry-cluster grant: grantClusterSession reads
  # access_control.token verbatim (ClusterSessionGrant.grant) to copy the
  # caller's session into the target cluster's own DB. Queued, the sanitizer
  # leaves it tokenless and the FIRST real data-target switch failed live
  # with "no authenticated session" (card 6a98a218) — the masqueradeAs
  # discovery replayed a fourth time.
  return true if /\.grantClusterSession$/i.test opId
  # masqueradeAs is (b) twice over, and neither half is optional:
  #   - it READS access_control.token verbatim (fetchMongoToken) to decide the
  #     no-chaining gate. queueSanitize strips the raw token at enqueue, so a
  #     queued call would find no row and, failing closed, refuse every
  #     masquerade — which is how this was found: the admin UI's socket path
  #     returned "Permission Denied" while the same call over HTTP succeeded.
  #   - it RETURNS a raw bearer token for ANOTHER user. removeOnFail keeps
  #     failed jobs resident in Redis, so queuing it would park a live
  #     credential at rest — the exact guarantee auth-token hashing exists to
  #     provide. Note /auth/i does NOT match "User.masqueradeAs".
  return true if /\.masqueradeAs$/i.test opId
  # scopeRole is (b): it reads access_control.token VERBATIM — hashToken(token)
  # is the row id it stamps/clears scopedRole on. queueSanitize strips the raw
  # token at enqueue, so a queued call finds no row and fails closed with
  # Permission Denied — found live on the seat (group:roles-design), the
  # masqueradeAs discovery replayed on the stand-down control.
  return true if /\.scopeRole$/i.test opId
  false

module.exports = {isCredentialBearingOperation}
